import type { ApiResponse } from '@shared/types'
import dayjs from 'dayjs'
import parsePhoneNumberFromString from 'libphonenumber-js'
import { useRef, useState } from 'react'
import type ReCAPTCHA from 'react-google-recaptcha'
import { useLocation, useNavigate } from 'router-v6'
import type { RLoginResponse, TwoFactorResendResponse } from 'v2/types/api/auth'
import { AnalyticsEvents, track } from 'v2/utils/analytics'
import {
  useLoginMutation,
  useTwoFactorAuthMutation,
  useTwoFactorResendMutation,
  useTwoFactorVerifyLoginMutation,
  useTwoFactorVerifySignupMutation,
} from '../queries'

interface LocationState {
  phoneNumber: string
  sessionUuid: string
  aomUUID: string
  accountID: string
  method: string
  email: string
  password: string
  orgID: string
  isSignup: boolean
  isSso?: boolean
  isInvite?: boolean
  nextUrl?: string
  isAuthenticatorDefault: boolean
  isAuthenticatorEnabled: boolean
  verificationPending: boolean
  originalLoginResponse: RLoginResponse
  region: string
  otpSentTime?: string
}

interface NavigationStateOverrides {
  sessionUuid?: string
  method?: string
  isAuthenticatorDefault?: boolean
  otpSentTime?: string
}

export const useTwoFactorVerification = () => {
  const navigate = useNavigate()
  const location = useLocation()

  // Check if location.state exists - if not, this page was accessed directly
  const locationState = location.state as LocationState | null
  const hasValidState = !!locationState

  const {
    phoneNumber = '',
    sessionUuid = '',
    aomUUID = '',
    accountID = '',
    method = '',
    email = '',
    password = '',
    orgID = '',
    isSignup = false,
    isSso = false,
    isInvite = false,
    nextUrl,
    isAuthenticatorDefault = false,
    isAuthenticatorEnabled = false,
    verificationPending = false,
    originalLoginResponse = {} as RLoginResponse,
    region = '',
    otpSentTime,
  } = locationState || {}

  const [isAuthenticatorMode, setIsAuthenticatorMode] = useState(isAuthenticatorDefault)
  const [isSmsResending, setIsSmsResending] = useState(false)
  const [isVoiceResending, setIsVoiceResending] = useState(false)
  const [timerStartTime, setTimerStartTime] = useState<string | null>(
    method === 'sms' || method === 'voice' ? otpSentTime || dayjs().toISOString() : null,
  )
  const recaptchaRef = useRef() as React.MutableRefObject<ReCAPTCHA>

  const verifyLoginMutation = useTwoFactorVerifyLoginMutation()
  const verifySignupMutation = useTwoFactorVerifySignupMutation()
  const resendMutation = useTwoFactorResendMutation()
  const twoFactorAuthMutation = useTwoFactorAuthMutation(recaptchaRef)
  const loginMutation = useLoginMutation(recaptchaRef)

  // Validation function to check if all required data is present
  const validateRequiredData = () => {
    // If there's no state at all, immediately fail validation
    if (!hasValidState) {
      return false
    }

    const requiredFields = {
      phoneNumber: !!phoneNumber,
      sessionOrAuthDefault: !!sessionUuid || !!isAuthenticatorDefault,
      aomUUID: !!aomUUID,
      method: !!method,
      email: !!email,
      password: isSignup || isSso || isInvite || !!password,
      orgOrAccount: !!orgID || !!accountID,
      region: !!region,
    }

    return Object.values(requiredFields).every(Boolean)
  }

  // Helper function to determine where to redirect when validation fails
  const getRedirectPath = () => {
    // If no state exists at all, default to login page
    if (!hasValidState) {
      return '/login'
    }

    // If we have some state, use the isSignup flag to determine redirect
    if (isSignup === false) {
      return '/login'
    }
    return '/two-factor-auth'
  }

  // Helper function to create navigation state object
  const createNavigationState = (overrides: NavigationStateOverrides) => ({
    phoneNumber,
    sessionUuid: overrides.sessionUuid || sessionUuid,
    aomUUID,
    accountID,
    method: overrides.method || method,
    email,
    password,
    orgID,
    isSignup,
    isSso,
    isInvite,
    nextUrl,
    isAuthenticatorDefault: overrides.isAuthenticatorDefault ?? isAuthenticatorDefault,
    isAuthenticatorEnabled,
    originalLoginResponse,
    verificationPending,
    region,
    otpSentTime: overrides.otpSentTime || otpSentTime,
  })

  const handleVerify = async (code: string) => {
    if (!code || !aomUUID || !accountID || !email || !region) {
      return
    }

    if (!isSignup && !isSso && !isInvite && !password) {
      return
    }

    if (!isAuthenticatorMode && !sessionUuid) {
      return
    }

    try {
      if (isSignup) {
        // Use signup-specific mutation — backend returns LoginResponse,
        // completeLoginProcess is called in mutation's onSuccess
        await verifySignupMutation.mutateAsync({
          session_uuid: sessionUuid,
          method: isAuthenticatorMode ? 'authenticator' : method,
          aom_uuid: aomUUID,
          account_id: Number(accountID),
          token: code,
          is_authenticator: isAuthenticatorMode,
          actor_email: email,
          verification_pending: verificationPending,
          region,
        })
      } else {
        // Use login-specific mutation
        await verifyLoginMutation.mutateAsync({
          session_uuid: sessionUuid,
          method: isAuthenticatorMode ? 'authenticator' : method,
          aom_uuid: aomUUID,
          account_id: Number(accountID),
          token: code,
          is_authenticator: isAuthenticatorMode,
          verification_pending: verificationPending,
          email,
          originalLoginResponse,
          region,
        })
      }
      // Fire only on first-time verification (signup path). Re-verification on
      // every subsequent login would inflate the count vs. the event name's intent.
      if (isSignup && !isAuthenticatorMode && (method === 'sms' || method === 'voice')) {
        const parsedPhone = phoneNumber ? parsePhoneNumberFromString(phoneNumber) : null
        track(AnalyticsEvents.PHONE_VERIFIED, {
          channel: method,
          country: parsedPhone?.country ?? '',
        })
      }
    } catch (_error) {
      // Error is already handled by the mutation's onError callback
    }
  }

  const executeResend = async ({
    resendMethod,
    setLoading,
    shouldCheckTimer = false,
    validateRequirements,
    getMutationCall,
  }: {
    resendMethod: 'sms' | 'voice'
    setLoading: (loading: boolean) => void
    shouldCheckTimer?: boolean
    validateRequirements: () => boolean
    getMutationCall: () => Promise<ApiResponse<TwoFactorResendResponse>>
  }) => {
    if (shouldCheckTimer && timerStartTime) return

    setLoading(true)

    if (!validateRequirements()) {
      setLoading(false)
      return
    }

    try {
      const response = await getMutationCall()
      setTimerStartTime(dayjs().toISOString())
      setLoading(false)
      navigate('/two-factor-verify', {
        state: createNavigationState({
          sessionUuid: response?.data.session_uuid,
          method: resendMethod,
          otpSentTime: dayjs().toISOString(),
        }),
        replace: true,
      })
    } catch (_error) {
      setLoading(false)
      // Error is already handled by the mutation's onError callback
    }
  }

  const handleResendCode = async () => {
    await executeResend({
      resendMethod: 'sms',
      setLoading: setIsSmsResending,
      shouldCheckTimer: true,
      validateRequirements: () => !!(aomUUID && accountID),
      getMutationCall: () =>
        resendMutation.mutateAsync({
          account_id: Number(accountID),
          method: 'sms',
          aom_uuid: aomUUID,
          is_signup: isSignup,
          region,
        }),
    })
  }

  const handleResendViaCall = async () => {
    await executeResend({
      resendMethod: 'voice',
      setLoading: setIsVoiceResending,
      validateRequirements: () => !!(phoneNumber && aomUUID && (orgID || accountID)),
      getMutationCall: async () => {
        if (isSignup) {
          return twoFactorAuthMutation.mutateAsync({
            phone_number: phoneNumber,
            method: 'voice',
            aom_uuid: aomUUID,
            org_id: Number(orgID),
            is_signup: isSignup,
            region,
            email,
          })
        }
        return resendMutation.mutateAsync({
          account_id: Number(accountID),
          method: 'voice',
          aom_uuid: aomUUID,
          is_signup: isSignup,
          region,
        })
      },
    })
  }

  const handleTimerStop = () => {
    setTimerStartTime(null)
  }

  const handleModeSwitch = async (toAuthenticator: boolean) => {
    if (toAuthenticator) {
      setIsAuthenticatorMode(true)
    } else {
      // Switch to SMS mode
      try {
        const response = await resendMutation.mutateAsync({
          account_id: Number(accountID),
          method: 'sms',
          aom_uuid: aomUUID,
          is_signup: isSignup,
          region,
        })
        setIsAuthenticatorMode(false)
        setTimerStartTime(dayjs().toISOString())
        navigate('/two-factor-verify', {
          state: createNavigationState({
            sessionUuid: response?.data.session_uuid,
            method: 'sms',
            isAuthenticatorDefault: false,
            otpSentTime: dayjs().toISOString(),
          }),
          replace: true,
        })
      } catch (_error) {
        // Error is already handled by the mutation's onError callback
      }
    }
  }

  return {
    // Location state data
    phoneNumber,
    sessionUuid,
    aomUUID,
    accountID,
    method,
    email,
    password,
    orgID,
    isSignup,
    nextUrl,
    isAuthenticatorDefault,
    isAuthenticatorEnabled,
    verificationPending,
    originalLoginResponse,
    region,
    otpSentTime,

    // Validation
    validateRequiredData,
    getRedirectPath,

    // State
    isAuthenticatorMode,
    setIsAuthenticatorMode,
    isSmsResending,
    isVoiceResending,
    timerStartTime,
    recaptchaRef,

    // Mutations
    verifyLoginMutation,
    verifySignupMutation,
    resendMutation,
    twoFactorAuthMutation,
    loginMutation,

    // Handlers
    handleVerify,
    handleResendCode,
    handleResendViaCall,
    handleTimerStop,
    handleModeSwitch,
  }
}
